import React from "react";
import { Button, ButtonVariant, Link, Text } from "@merge-api/merge-javascript-shared";
import coastLogo from "../../../../assets/img/coastpay_logo.jpeg";
import { MoreHorizontal } from "lucide-react";
import { MERGE_END_USER_TERMS_PATH } from "../../../../router/RouterUtils";

export const QBO_SQUARE_IMAGE_URL =
  "https://merge-api-public.s3.amazonaws.com/media/QuickBooks-square_Uf26B71.png";

const QBOBankFeedAuthorizeScreen = () => {
  const params = new URLSearchParams(window.location.search);
  const data_provider_login_uri = params.get("data_provider_login_uri") ?? ""; // customer login page
  const original_state = params.get("state") ?? ""; // state from intuit qbo
  const client_id = params.get("client_id") ?? ""; // client id for the integration
  const redirect_url = params.get("redirect_url") ?? ""; // QBO callback url

  // TODO: Replace with actual organization name
  const customerName = "Coast";

  // Check if all required params are present
  const isValidParams = data_provider_login_uri && redirect_url && original_state;

  const handleContinue = () => {
    // Create state object to carry context through the OAuth redirect chain.
    // data_provider_login_uri is included so the backend can identify the
    // organization on callback — it's unique per customer.
    const stateObject = {
      original_state,
      client_id,
      redirect_url,
      data_provider_login_uri,
    };

    // Encode the state object as JSON
    const encodedState = encodeURIComponent(JSON.stringify(stateObject));

    // Redirect to data_provider_login with state and redirect_uri for Intuit callback
    const customerUrl = `${data_provider_login_uri}?state=${encodedState}&redirect_uri=${encodeURIComponent(
      "https://app.merge.dev/intuit/callback",
    )}`;
    window.location.href = customerUrl;
  };

  const handleCancel = () => {
    window.close();
  };

  return (
    <div className="d-flex align-items-center justify-content-center min-h-screen">
      <div className="col-12 col-md-5 col-xl-4 my-24 rounded-2xl bg-white border-gray-10 border-[1px] drop-shadow-sm px-12 pt-12 pb-6 max-w-[488px]">
        {/* Logo */}
        <div className="w-full flex justify-center mb-6">
          <div className="flex items-center justify-center space-x-2">
            <img
              className="w-12 h-12 object-cover rounded-[12px] border-[0.5px] border-black border-opacity-[.08] shadow-[0px_5px_8px_-3px_#0000000D,0px_0.5px_1px_0px_#00000017]"
              src={coastLogo} // TODO: Replace with organization logo (pulled from white-label guides?)
              alt="Company logo"
            />
            <MoreHorizontal className="text-gray-20" size={36} />
            <img
              className="w-12 h-12 object-cover rounded-[12px] border-[0.5px] border-black border-opacity-[.08] shadow-[0px_5px_8px_-3px_#0000000D,0px_0.5px_1px_0px_#00000017]"
              src={QBO_SQUARE_IMAGE_URL}
              alt="QuickBooks"
            />
          </div>
        </div>

        {/* Title */}
        <Text variant="h3" className="mb-8 text-center">
          Connect Your {customerName} Account to QuickBooks
        </Text>

        {/* Data Usage Section */}
        <div className="mb-6">
          <Text variant="h5" className="mb-3">
            How your data is used
          </Text>
          <Text className="text-gray-40 leading-relaxed m-0">
            When you link your {customerName} account, you'll be able to access your transactions in
            your connected accounting software for quick and easy reconciliation.
          </Text>
        </div>

        {/* Data Shared Section */}
        <div className="mb-8 flex flex-col">
          <Text variant="h5" className="mb-3">
            What data is shared
          </Text>
          <Text className="text-gray-40 leading-relaxed mb-3">
            Your connected accounting software will access your data until you withdraw consent. You
            can unlink your account any time, and your credentials won't be shared.
          </Text>
          <Text className="text-gray-40 leading-relaxed mb-3">Bank feeds</Text>
        </div>

        {/* Buttons */}
        <div className="flex flex-col gap-3">
          <Button
            variant={ButtonVariant.PrimaryGray}
            onClick={handleContinue}
            className="w-full p-3 rounded-md"
            disabled={!isValidParams}
          >
            Continue to login
          </Button>
          <Button
            variant={ButtonVariant.TertiaryWhite}
            onClick={handleCancel}
            className="w-full p-3 rounded-md"
          >
            Cancel
          </Button>
        </div>

        {/* Footer Text */}
        <div className="flex justify-center items-center pt-3">
          <span className="text-gray-40 text-11 font-normal leading-4">
            By continuing, you agree to Merge's{" "}
            <Link className="underline text-black" href={MERGE_END_USER_TERMS_PATH}>
              End User Terms
            </Link>
          </span>
        </div>
      </div>
    </div>
  );
};

export default QBOBankFeedAuthorizeScreen;
